import React, { useState, useEffect } from 'react';
import { CONFIG } from '../config/environment';

interface Booking {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email?: string;
  amount?: number;
  collected?: number;
  date?: string;
}

interface StripePayment {
  id: string;
  amount: number;
  currency: string;
  customerName: string;
  customerEmail: string | null;
  description: string;
  created: string;
}

interface Match {
  payment: StripePayment;
  confidence: number;
  matchType: 'exact' | 'email' | 'fuzzy';
}

interface Suggestion {
  booking: Booking;
  matches: Match[];
}

interface StripePaymentMatcherProps {
  userId: string;
  bookingsData: any[];
  onMatchAccepted: (bookingId: string, amount: number) => void;
  onClose: () => void;
}

const StripePaymentMatcher: React.FC<StripePaymentMatcherProps> = ({ 
  userId, 
  bookingsData, 
  onMatchAccepted,
  onClose 
}) => {
  const [connected, setConnected] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [acceptedMatches, setAcceptedMatches] = useState<Record<string, string>>({});
  const [bulkThreshold, setBulkThreshold] = useState<number>(95);

  useEffect(() => {
    checkConnection();
    loadAcceptedMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const checkConnection = async () => {
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/stripe-matching/connection/${userId}`);
      const data = await response.json();
      setConnected(data.connected);
      
      if (data.connected) {
        loadSuggestions();
      }
    } catch (err) {
      console.error('Error checking connection:', err);
    }
  };

  const loadAcceptedMatches = async () => {
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/stripe-matching/accepted-matches/${userId}`);
      const data = await response.json();
      
      const matchMap: Record<string, string> = {};
      Object.entries(data.matches).forEach(([bookingId, match]: [string, any]) => {
        matchMap[bookingId] = match.paymentId;
      });
      setAcceptedMatches(matchMap);
    } catch (err) {
      console.error('Error loading accepted matches:', err);
    }
  };

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your Stripe API key');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/stripe-matching/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          stripeApiKey: apiKey
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to connect Stripe account');
      }

      setConnected(true);
      setApiKey('');
      loadSuggestions();
    } catch (err: any) {
      console.error('Connection error:', err);
      setError(err.message || 'Failed to connect to Stripe');
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      // Extract booking data
      const headers = bookingsData[0];
      
      const firstNameIndex = headers.findIndex((h: string) => h === 'First Name');
      const lastNameIndex = headers.findIndex((h: string) => h === 'Last Name');
      const emailIndex = headers.findIndex((h: string) => h.toLowerCase().includes('email'));
      const amountIndex = headers.findIndex((h: string) => h === 'Close Amount');
      const dateIndex = headers.findIndex((h: string) => h === 'DateTime');
      const idIndex = headers.findIndex((h: string) => h === 'ID' || h === 'Booking ID');
      
      const bookings = bookingsData.slice(1).map((row, index) => {
        const firstName = row[firstNameIndex] || '';
        const lastName = row[lastNameIndex] || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        return {
          id: row[idIndex] || `booking-${index}`,
          firstName,
          lastName,
          fullName,
          email: row[emailIndex] || null,
          amount: parseFloat(row[amountIndex]?.toString().replace(/[$,]/g, '') || '0'),
          date: row[dateIndex] || null
        };
      }).filter(b => b.fullName && b.fullName !== '');

      const response = await fetch(`${CONFIG.API_BASE_URL}/api/stripe-matching/payments-for-matching`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          bookings
        })
      });

      if (!response.ok) {
        throw new Error('Failed to load payment suggestions');
      }

      const data = await response.json();
      setSuggestions(data.suggestions);
    } catch (err: any) {
      console.error('Error loading suggestions:', err);
      setError('Failed to load payment suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptMatch = async (bookingId: string, payment: StripePayment) => {
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/stripe-matching/accept-match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          bookingId,
          paymentId: payment.id,
          amount: payment.amount
        })
      });

      if (response.ok) {
        setAcceptedMatches(prev => ({
          ...prev,
          [bookingId]: payment.id
        }));
        
        // Notify parent component
        onMatchAccepted(bookingId, payment.amount);
      }
    } catch (err) {
      console.error('Error accepting match:', err);
    }
  };

  const handleBulkAccept = async () => {
    if (!window.confirm(`Accept all matches with ${bulkThreshold}% confidence or higher?`)) {
      return;
    }

    setLoading(true);
    try {
      // Collect all matches above threshold
      const matchesToAccept = suggestions.flatMap(suggestion => 
        suggestion.matches
          .filter(match => match.confidence >= bulkThreshold && !acceptedMatches[suggestion.booking.id])
          .map(match => ({
            bookingId: suggestion.booking.id,
            paymentId: match.payment.id,
            amount: match.payment.amount,
            confidence: match.confidence
          }))
      );

      const response = await fetch(`${CONFIG.API_BASE_URL}/api/stripe-matching/bulk-accept-matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          matches: matchesToAccept,
          threshold: bulkThreshold
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update accepted matches state
        const newAcceptedMatches = { ...acceptedMatches };
        matchesToAccept.forEach(match => {
          newAcceptedMatches[match.bookingId] = match.paymentId;
          // Notify parent component
          onMatchAccepted(match.bookingId, match.amount);
        });
        setAcceptedMatches(newAcceptedMatches);
        
        alert(data.message);
      }
    } catch (err) {
      console.error('Error bulk accepting matches:', err);
      setError('Failed to bulk accept matches');
    } finally {
      setLoading(false);
    }
  };

  const getEligibleBulkMatches = () => {
    return suggestions.reduce((count, suggestion) => {
      return count + suggestion.matches.filter(match => 
        match.confidence >= bulkThreshold && !acceptedMatches[suggestion.booking.id]
      ).length;
    }, 0);
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return '#10b981';
    if (confidence >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const formatCurrency = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toLowerCase()
    }).format(amount);
  };

  if (!connected) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <h2>Connect Stripe for Payment Matching</h2>
          
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            Connect your Stripe account to automatically match payments with bookings.
          </p>

          {error && (
            <div style={{
              backgroundColor: '#fee',
              color: '#c00',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Stripe API Key (Restricted Key Recommended):
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="rk_test_... or sk_test_..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  paddingRight: '3rem',
                  fontSize: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: '0.5rem',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {showApiKey ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={handleConnect}
              disabled={loading || !apiKey.trim()}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: loading || !apiKey.trim() ? 'not-allowed' : 'pointer',
                opacity: loading || !apiKey.trim() ? 0.6 : 1
              }}
            >
              {loading ? 'Connecting...' : 'Connect Stripe'}
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '1200px',
        height: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>Stripe Payment Matching</h2>
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
          
          {/* Bulk Actions */}
          {suggestions.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              backgroundColor: '#f3f4f6',
              borderRadius: '6px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label>Bulk accept threshold:</label>
                <input
                  type="number"
                  min="60"
                  max="100"
                  value={bulkThreshold}
                  onChange={(e) => setBulkThreshold(parseInt(e.target.value))}
                  style={{
                    width: '80px',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px'
                  }}
                />
                <span>%</span>
              </div>
              
              <button
                onClick={handleBulkAccept}
                disabled={loading || getEligibleBulkMatches() === 0}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading || getEligibleBulkMatches() === 0 ? 'not-allowed' : 'pointer',
                  opacity: loading || getEligibleBulkMatches() === 0 ? 0.6 : 1
                }}
              >
                {loading ? 'Processing...' : `Accept ${getEligibleBulkMatches()} matches ≥${bulkThreshold}%`}
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div>Loading payment suggestions...</div>
            </div>
          ) : error ? (
            <div style={{
              backgroundColor: '#fee',
              color: '#c00',
              padding: '1rem',
              borderRadius: '4px'
            }}>
              {error}
            </div>
          ) : (
            <div>
              <p style={{ marginBottom: '1.5rem', color: '#666' }}>
                Found {suggestions.length} bookings with potential payment matches. Only bookings with matches are shown.
              </p>

              {suggestions.map((suggestion) => (
                <div key={suggestion.booking.id} style={{
                  marginBottom: '2rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}>
                    <div>
                      <h3 style={{ margin: 0 }}>
                        Booking: {suggestion.booking.firstName} {suggestion.booking.lastName}
                      </h3>
                      {suggestion.booking.email && (
                        <small style={{ color: '#666' }}>{suggestion.booking.email}</small>
                      )}
                      {suggestion.booking.date && (
                        <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
                          Booking Date: {new Date(suggestion.booking.date).toLocaleDateString()}
                        </small>
                      )}
                    </div>
                    {acceptedMatches[suggestion.booking.id] && (
                      <span style={{
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem'
                      }}>
                        ✓ Matched
                      </span>
                    )}
                  </div>

                  {suggestion.matches.length > 0 ? (
                    <div style={{ display: 'grid', gap: '0.5rem' }}>
                      {suggestion.matches.map((match) => (
                        <div key={match.payment.id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.75rem',
                          backgroundColor: acceptedMatches[suggestion.booking.id] === match.payment.id ? '#f0fdf4' : '#f9fafb',
                          borderRadius: '6px',
                          border: acceptedMatches[suggestion.booking.id] === match.payment.id ? '1px solid #86efac' : '1px solid #e5e7eb'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              <strong>{match.payment.customerName}</strong>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{
                                  backgroundColor: getConfidenceColor(match.confidence),
                                  color: 'white',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem'
                                }}>
                                  {match.confidence}%
                                </span>
                                <span style={{
                                  color: '#059669',
                                  fontSize: '0.875rem',
                                  fontWeight: '500'
                                }}>
                                  {match.matchType}
                                </span>
                              </div>
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                              {formatCurrency(match.payment.amount, match.payment.currency)} • 
                              {new Date(match.payment.created).toLocaleDateString()}
                              {match.payment.description && ` • ${match.payment.description}`}
                            </div>
                          </div>
                          {!acceptedMatches[suggestion.booking.id] && (
                            <button
                              onClick={() => handleAcceptMatch(suggestion.booking.id, match.payment)}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.875rem'
                              }}
                            >
                              Accept Match
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#666', fontStyle: 'italic' }}>
                      No matching payments found
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripePaymentMatcher;